import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { aboutContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

export default function AboutSection() {
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();

  return (
    <section className="relative bg-background-50 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Text */}
          <div className={`flex-1 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-primary-400 tracking-wide">
                {aboutContent.titleCn}
              </h3>
              <h2 className="mt-1 text-3xl md:text-4xl lg:text-5xl font-black text-foreground-50 font-heading uppercase tracking-wider">
                {aboutContent.titleEn}
              </h2>
              <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed whitespace-pre-line">
              {aboutContent.description}
            </p>
            {/* Stats */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {aboutContent.stats.map((stat) => (
                <div key={stat.subLabel} className="text-center py-3 px-2 rounded-lg bg-background-100 border border-background-200/50">
                  <p className="text-xl md:text-2xl font-black text-primary-400 font-heading">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-xs text-foreground-300 font-label">
                    {stat.subLabel}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to={aboutContent.ctaHref}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-foreground-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {aboutContent.cta}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          {/* Image */}
          <div className={`flex-1 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative rounded-lg overflow-hidden border border-background-200/50">
              <img
                src={aboutContent.image}
                alt="关于我们"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}